import type { LoaderComponent } from 'next/dynamic'
import dynamic from 'next/dynamic'
import type { ComponentType } from 'react'

export interface ImportRetryOptions {
    retryAttempts?: number
    delay?: number
}

// we can change these as needed but 3 attempts
//  seem reasonable for now
const DEFAULT_RETRIES = 3
const DEFAULT_RETRY_DELAY_MS = 500

export async function loadWithRetry<T>(
    loader: () => Promise<T>,
    {
        retryAttempts = DEFAULT_RETRIES,
        delay = DEFAULT_RETRY_DELAY_MS,
    }: ImportRetryOptions = {},
): Promise<T> {
    try {
        return await loader()
    } catch (error) {
        if (retryAttempts <= 0) {
            throw error
        }
        await new Promise((resolve) => setTimeout(resolve, delay))
        return loadWithRetry(loader, {
            retryAttempts: retryAttempts - 1,
            delay,
        })
    }
}

/**
 * Retry dynamic imports when they fail due to network conditions
 * @param loader
 * @param options
 * @returns
 */
export function dynamicWithRetry<P = object>(
    loader: () => LoaderComponent<P>,
    options?: ImportRetryOptions,
): ComponentType<P> {
    return dynamic<P>(() => loadWithRetry(loader, options))
}

export default dynamicWithRetry
